import React, { Fragment } from 'react';

import { Button, Link } from '@zapier/design-system';

import { UpgradeButtonToPricingPage } from '@zapier/upsells/src/components/UpgradeModal/UpgradeModalLayout/UpgradeNudge/UpgradeButtonToPricingPage';
import { Plan } from '@zapier/upsells/src/types';

const ContinueButton = ({ onClick }: { onClick?: () => void }) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <Button color="greyAlt" onClick={handleClick}>
      Continue
    </Button>
  );
};

/**
 * Note: Upgrade buttons in these views take the user to the upgrade
 * page rather than doing the upgrade inline because these nudges could
 * be used in `web-ssr`, and the payment app doesn't work in `web-ssr`.
 */
const views = {
  ['AUTO_UPGRADE']: ({ autoUpgradeToPlan }: { autoUpgradeToPlan: Plan }) => ({
    bodyText: (
      <Fragment>
        Because you've opted into the{' '}
        <Link href="/l/automatic-task-upgrades">automatic task upgrade</Link>{' '}
        feature, if you reach 100% of your task limit, you will be upgraded to
        the {autoUpgradeToPlan.type} plan with{' '}
        {Number(autoUpgradeToPlan.tasks).toLocaleString()} tasks.{' '}
        <strong>No action from you is required.</strong>
      </Fragment>
    ),
    buttons: [
      <Button
        color="secondary"
        href="/app/settings/billing"
        key="manage-auto-upgrade"
      >
        Manage setting
      </Button>,

      <Button color="primary" href="/app/history/usage" key="see-task-history">
        See task history
      </Button>,
    ],
    type: 'AUTO_UPGRADE',
  }),

  ['DEFAULT']: ({
    currentPlan,
    destinationPlan,
    tasksRemaining,
  }: {
    currentPlan: Plan;
    destinationPlan: Plan;
    tasksRemaining: number;
  }) => ({
    bodyText: (
      <Fragment>
        You have {tasksRemaining.toLocaleString()} task
        {tasksRemaining !== 1 && <span>s</span>} left until your monthly limit
        is reached. Upgrade your plan now to avoid your{' '}
        <Link
          href="/help/manage/history/view-and-manage-your-zap-history#holding"
          target="_blank"
        >
          Zap runs being held
        </Link>
        .
      </Fragment>
    ),
    buttons: [
      <UpgradeButtonToPricingPage
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,

      <ContinueButton />,
    ],
    type: 'DEFAULT',
  }),
};

type Props = {
  autoUpgradeToPlan: Plan | null | undefined;
  currentPlan: Plan;
  destinationPlan: Plan;
  isAutoUpgradeEnabled: boolean;
  tasksRemaining: number;
};

export const getActiveView = ({
  autoUpgradeToPlan,
  currentPlan,
  destinationPlan,
  isAutoUpgradeEnabled,
  tasksRemaining,
}: Props) =>
  isAutoUpgradeEnabled && autoUpgradeToPlan
    ? views.AUTO_UPGRADE({
        autoUpgradeToPlan,
      })
    : views.DEFAULT({
        currentPlan,
        destinationPlan,
        tasksRemaining,
      });
